import type React from 'react';
import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { LocalSignerGoal } from '#src/app/shared/components/localSignerGoal';
import { LocalSignerShare } from '#src/app/shared/components/localSignerShare';

import { PetitionMedia } from '../PetitionMedia';
import { useCarouselScrollTracking } from '../SharingGoalFlow/components/GoalShareActions/hooks/useCarouselScrollTracking';

import { CurrentShareScreen } from './components/CurrentShareScreen';
import { useConstituentShareData } from './hooks/useConstituentShareData';

/**
 * Constituents-in-PSF (CHANGE-100768) variant surface, rendered on the share page for an identified
 * local constituent enrolled in `v2` or `v4`. Both arms are a two-step flow (share then goal); `v2`
 * shows the new local-signer share screen, `v4` keeps the current share screen, and both end on the
 * new local-signer goal screen.
 *
 * The variant lives inside the control page shell: the `PetitionMedia` drawer sits above the screen
 * (as it does for control), and the page's own footer provides the single "skip" link, so the variant
 * screens carry no skip/close of their own - only `Continue` advances share -> goal. `v4`'s share step
 * reuses `CurrentShareScreen` (the control body), which renders its own `PetitionMedia`, so it is not
 * wrapped again here.
 *
 * Enrollment (treat + exposure) is fired once by `useTreatConstituentPsfExperiment` at the `PageBody`
 * level so control is enrolled too; this component only renders the differing screens.
 */
type Props = Readonly<{ arm: 'v2_new_share' | 'v4_new_goal' }>;

type Step = 'share' | 'goal';

export const ConstituentShareFlow: React.FC<Props> = ({ arm }) => {
	const { shareActions, location, userName, isFirstLocalSigner, otherSignerCount } = useConstituentShareData();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePsfSharePageData();
	const track = useTracking();
	const trackCarouselScroll = useCarouselScrollTracking();
	const [step, setStep] = useState<Step>('share');

	// Goal slider + channel carousel reuse the control goal-screen event names so the funnel is
	// comparable across arms (CHANGE-100773); the slider mirrors `useSetGoalStep`'s payload.
	const handleGoalChange = useCallback(
		(value: number) => {
			void track('psf_share_goal_slider_change', { goal_value: value, petition_id: petitionId });
		},
		[track, petitionId],
	);

	if (step === 'goal') {
		return (
			<>
				<PetitionMedia />
				<LocalSignerGoal
					location={location}
					userName={userName}
					shareActions={shareActions}
					onCarouselScroll={trackCarouselScroll}
					onGoalChange={handleGoalChange}
				/>
			</>
		);
	}

	if (arm === 'v4_new_goal') {
		return <CurrentShareScreen onContinue={() => setStep('goal')} />;
	}

	return (
		<>
			<PetitionMedia />
			<LocalSignerShare
				hypothesis="motivation"
				location={location}
				isFirstLocalSigner={isFirstLocalSigner}
				signerCount={otherSignerCount}
				shareActions={shareActions}
				onContinue={() => setStep('goal')}
			/>
		</>
	);
};
